import {memo} from 'react';

import styled from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import {flipOnLTR} from '~/shared/theme/utils';
import AddressAutoComplete from '~/shared/components/CustomAddressAutoComplete';
import {body16Normal} from '~/shared/theme/typography';
import {Address, LocalAddress} from '~/shared/store/models';
import {AddressPartsFromKey} from '~/shared/utils/address';
import {AddressFinderVariant} from '~/shared/consts/addressConsts';
import Input from '~/shared/components/Input';
import {AddressFinderLocation} from '~/shared/services/analytics/analyticsModels';

import useAddressFinder from './useAddressFinder';

interface AddressFinderProps {
  location: AddressFinderLocation;
  placeholder?: string;
  itemHeight?: number;
  isAddAddressMode?: boolean;
  onChange: (address?: Address | LocalAddress | AddressPartsFromKey) => void;
  setDisableSubmit: (value: boolean) => void;
  disableSubmit: boolean;
  submit: (address?: Address | LocalAddress | AddressPartsFromKey) => Promise<void>;
  isMaxMobile?: boolean;
  disableAutoFocus: boolean;
  variant?: AddressFinderVariant;
}

const Root = styled.div`
  display: flex;
  flex: 1 1 auto;
`;

const HouseNumberInput = styled(Input)<{isAddAddressMode?: boolean; variant: AddressFinderVariant; isError?: boolean}>`
  width: ${({variant}) => (variant === AddressFinderVariant.PIE ? '88px' : '56px')};
  height: ${({variant}) => (variant === AddressFinderVariant.PIE ? '56px' : '100%')};
  border: ${({theme, variant, isError}) => (variant === AddressFinderVariant.PIE ? `1px solid ${isError ? theme.colors.contentError : theme.colors.background}` : 'none')};
  ${({variant}) => variant === AddressFinderVariant.PIE && `
    border-radius: 12px;
  `}
  font-size: ${({isAddAddressMode}) => (isAddAddressMode ? 16 : 18)}px;
  text-align: center;
  color: ${({theme}) => theme.colors.secondary};

  ::placeholder {
    color: ${({theme}) => theme.colors.secondary};
  }

  ${flipOnLTR`
    ${({variant, theme}) => variant === AddressFinderVariant.LEGACY && `border-right: 1px solid ${theme.colors.background};`}
    ${({variant}) => variant === AddressFinderVariant.PIE && 'margin-right: 8px;'}
  `}
`;

const PieErrorMessage = styled.div.attrs({
  role: 'alert',
})<{changeDirection?: boolean}>`
  ${body16Normal}
  ${({theme, changeDirection}) => changeDirection && `direction: ${theme.isLTR ? 'rtl' : 'ltr'};`}
  color: ${({theme}) => theme.colors.contentError};
  margin-top: 8px;
`;

const ErrorIndicatorMobileAndDropDown = styled.div.attrs({
  name: 'errorIndicator',
  role: 'alert',
})`
  z-index: 3;
  color: ${({theme}) => theme.colors.error};
  border: 1px solid ${({theme}) => theme.colors.error};
  background: ${({theme}) => theme.colors.surface};
  padding: 12px;
  top: calc(100% + 16px);
  ${body16Normal}
  position: absolute;
  ${flipOnLTR`
    left: 16px;
  `}

  &::after {
    content: '';
    z-index: 1;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid ${({theme}) => theme.colors.error};
    position: absolute;
    top: -8px;
    ${flipOnLTR`
      left: 4px;
    `}
  }

  &::before {
    content: '';
    z-index: 2;
    width: 0;
    height: 0;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-bottom: 7px solid ${({theme}) => theme.colors.surface};
    position: absolute;
    top: -7px;
    ${flipOnLTR`
      left: 5px;
    `}
  }
`;

const AddressFinder = ({
  location,
  placeholder,
  itemHeight,
  isAddAddressMode,
  onChange,
  setDisableSubmit,
  disableSubmit,
  submit: submitForm,
  isMaxMobile,
  disableAutoFocus,
  variant = AddressFinderVariant.LEGACY,
}: AddressFinderProps) => {
  const {t} = getLocalizationService();

  const {
    errorMessage,
    showAddressNumberInput,
    rootRef,
    onPlaceSelected,
    resetAddress,
    getCurrentLocation,
    handleCustomHouseNumberChange,
    addressSelectorRef,
    isHouseNumberError,
    isAddressAutoCompleteError,
  } = useAddressFinder({
    variant,
    submitForm,
    location,
    onChange,
    setDisableSubmit,
    disableAutoFocus,
    disableSubmit,
  });

  return (
    <>
      <Root ref={rootRef}>
        <AddressAutoComplete
          inputRef={addressSelectorRef}
          onCurrentLocationClick={getCurrentLocation}
          {...{
            placeholder,
            onPlaceSelected,
            isMaxMobile,
            resetAddress,
            itemHeight,
            isAddAddressMode,
            location,
            variant,
            isError: isAddressAutoCompleteError,
            showAddressNumberInput,
          }}
        />
        {(variant === AddressFinderVariant.LEGACY && errorMessage) && <ErrorIndicatorMobileAndDropDown>{errorMessage}</ErrorIndicatorMobileAndDropDown>}
        {showAddressNumberInput && (
        <HouseNumberInput
          placeholder={t('house')}
          inputType="text"
          defaultValue=""
          onChange={e => handleCustomHouseNumberChange(e.target.value)}
          maxLength={5}
          isAddAddressMode={isAddAddressMode}
          variant={variant}
          isError={isHouseNumberError}
        />
      )}
      </Root>
      {(variant === AddressFinderVariant.PIE && errorMessage) && <PieErrorMessage changeDirection={isHouseNumberError}>{errorMessage}</PieErrorMessage>}
    </>
  );
};

export default memo(AddressFinder);
